import { z } from 'zod'
import { loginRequestSchema } from './auth.js'
import {
  type Locale,
  localeSchema,
  moneySchema,
  optionalText,
  pageQuerySchema,
  versionSchema,
} from './common.js'
import { companyProfileSchema } from './company.js'
import type { MessageChannel } from './messaging.js'
import { DEFAULT_MODULES, MODULES, type ModuleKey } from './permissions.js'
import { passwordSchema } from './users.js'

/**
 * The super-admin (platform) console, ADR-019: operators of the whole service, separate from tenant
 * users. Routes live under /api/v1/platform and use their own session.
 */

/** OWNER: everything. ADMIN: everything but suspending tenants and changing their modules. SUPPORT: read and assist. */
export const PLATFORM_ROLES = ['OWNER', 'ADMIN', 'SUPPORT'] as const
export type PlatformRole = (typeof PLATFORM_ROLES)[number]
export const platformRoleSchema = z.enum(PLATFORM_ROLES)

const RANK: Record<PlatformRole, number> = { SUPPORT: 1, ADMIN: 2, OWNER: 3 }
/** Whether `role` is at least `min`. */
export function platformRoleAtLeast(role: PlatformRole, min: PlatformRole): boolean {
  return RANK[role] >= RANK[min]
}

// ── sign-in and profile ─────────────────────────────────────────────────────

export const platformLoginSchema = loginRequestSchema
export type PlatformLogin = z.input<typeof platformLoginSchema>

export interface PlatformAdmin {
  id: string
  email: string
  name: string
  role: PlatformRole
}

export interface PlatformSessionInfo {
  admin: PlatformAdmin
  /** The profile's version, for PATCH /platform/profile. */
  version: number
}

/** GET /platform/auth/methods: how the console's sign-in page can sign a super admin in on this server. */
export interface PlatformSignInMethods {
  /** Email and password (off with SUPERADMIN_LOCAL_LOGIN=false). */
  password: boolean
  /** "Sign in with cloudTech Hub" (ADR-042): the server's SSO_* settings are complete. */
  hub: boolean
}

/** Where the browser starts a cloudTech Hub sign-in; also the start URL of the console's card in the Hub. */
export const PLATFORM_HUB_START_PATH = '/api/v1/platform/auth/sso/start'

/** Why a Hub sign-in came back to the sign-in page (`/platform/login?sso_error=…`). */
export const PLATFORM_HUB_ERRORS = [
  'no_superadmin',
  'not_mfa',
  'state',
  'hub_denied',
  'sso_not_configured',
  'sso_failed',
] as const
export type PlatformHubError = (typeof PLATFORM_HUB_ERRORS)[number]

const emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(254))

/** The admin's own name and email. Changing the email needs the current password. */
export const platformProfileSchema = z.object({
  name: z.string().trim().min(1).max(200),
  email: emailSchema,
  currentPassword: z.string().max(256).optional(),
  version: versionSchema,
})
export type PlatformProfileUpdate = z.input<typeof platformProfileSchema>

// ── tenants ─────────────────────────────────────────────────────────────────

export const TENANT_STATUSES = ['ACTIVE', 'SUSPENDED'] as const
export type TenantStatus = (typeof TENANT_STATUSES)[number]

/** Slugs that name parts of the service rather than a company (legacy reserved the same words). */
export const RESERVED_TENANT_SLUGS = [
  'admin',
  'api',
  'app',
  'www',
  'mail',
  'platform',
  'console',
  'static',
  'assets',
  'support',
  'help',
  'status',
  'login',
] as const

export const tenantSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/,
    'Use 1 to 40 lowercase letters, digits and hyphens, starting and ending with a letter or digit',
  )
  .refine((v) => !(RESERVED_TENANT_SLUGS as readonly string[]).includes(v), 'This name is reserved')

const moduleSchema = z.enum(MODULES as [ModuleKey, ...ModuleKey[]])
const modulesSchema = z.array(moduleSchema).transform((list) => MODULES.filter((m) => list.includes(m)))

/** The company's legal identity, as in Settings → Company (without the display name and version). */
export const tenantProfileSchema = companyProfileSchema.pick({
  legalName: true,
  legalNameAr: true,
  vatNumber: true,
  crNumber: true,
  address: true,
  addressAr: true,
  phone: true,
  email: true,
})
export type TenantProfileInput = z.input<typeof tenantProfileSchema>

const tenantNameSchema = z.string().trim().min(1).max(200)

// ── data residency (01 §11) ─────────────────────────────────────────────────

/**
 * Where a tenant's data lives: `OM` (in Oman; usable only when the whole deployment runs there,
 * `DEPLOYMENT_REGION=OM`, else 422 ZONE_NOT_CONFIGURED) or `GLOBAL`.
 */
export const RESIDENCY_ZONES = ['OM', 'GLOBAL'] as const
export type ResidencyZone = (typeof RESIDENCY_ZONES)[number]
export const residencyZoneSchema = z.enum(RESIDENCY_ZONES)

export function isResidencyZone(value: string): value is ResidencyZone {
  return (RESIDENCY_ZONES as readonly string[]).includes(value)
}

export const platformTenantCreateSchema = z.object({
  slug: tenantSlugSchema,
  name: tenantNameSchema,
  nameAr: optionalText(200),
  defaultLocale: localeSchema.default('en'),
  profile: tenantProfileSchema,
  /** Default: every module but the opt-in ones (AI assistant, loyalty points), which are ticked on purpose. */
  modules: modulesSchema.default([...DEFAULT_MODULES]),
  /** Where the company's data lives (default GLOBAL). */
  residencyZone: residencyZoneSchema.default('GLOBAL'),
  /** The company's first administrator (Administrator role, every permission). */
  admin: z.object({
    name: z.string().trim().min(1).max(200),
    email: emailSchema,
    password: passwordSchema,
  }),
})
export type PlatformTenantCreate = z.input<typeof platformTenantCreateSchema>

/** Name, language and profile. `version` is the profile's (Settings → Company shares it). */
export const platformTenantUpdateSchema = z.object({
  name: tenantNameSchema,
  nameAr: optionalText(200),
  defaultLocale: localeSchema,
  profile: tenantProfileSchema,
  version: versionSchema,
})
export type PlatformTenantUpdate = z.input<typeof platformTenantUpdateSchema>

/** Suspend / activate: the reason is kept in the operator audit. */
export const platformTenantStatusChangeSchema = z.object({
  reason: z.string().trim().min(3).max(500),
})
export type PlatformTenantStatusChange = z.input<typeof platformTenantStatusChangeSchema>

export const platformTenantModulesSchema = z.object({ modules: modulesSchema })
export type PlatformTenantModules = z.input<typeof platformTenantModulesSchema>

export const PLATFORM_TENANT_SORT_FIELDS = ['name', 'slug', 'status', 'createdAt', 'users'] as const
export const platformTenantListQuerySchema = pageQuerySchema.extend({
  status: z.enum(TENANT_STATUSES).optional(),
})
export type PlatformTenantListQuery = z.input<typeof platformTenantListQuerySchema>

export interface PlatformTenantListItem {
  id: string
  slug: string
  name: string
  nameAr: string | null
  status: TenantStatus
  modules: ModuleKey[]
  /** Users who can sign in (ACTIVE). */
  activeUsers: number
  /** Latest sign-in of any of its users. */
  lastLoginAt: string | null
  createdAt: string
}

export interface PlatformTenantProfile {
  legalName: string | null
  legalNameAr: string | null
  vatNumber: string | null
  crNumber: string | null
  address: string | null
  addressAr: string | null
  phone: string | null
  email: string | null
}

export interface PlatformTenant extends PlatformTenantListItem {
  defaultLocale: Locale
  timezone: string
  currency: string
  profile: PlatformTenantProfile
  /** The profile's version (PATCH). */
  version: number
  updatedAt: string
  /**
   * Approval requests in progress (waiting, blocked or finishing). Turning the workflows module, or the
   * module of the documents they route (Purchasing), off puts them on hold: their documents stay locked
   * until it is turned back on.
   */
  activeApprovalRequests: number
  /** Till shifts still open: while POS is off nobody can count or close them. */
  openPosShifts: number
  /** Payments technicians reported that nobody has verified yet: verifying needs Service and Sales. */
  reportedServiceCollections: number
  /** The end of the latest fiscal year (a business date; null before the first posting). */
  latestFiscalYearEnd: string | null
  /** The books' lock date (null when none is set). Only Accounting can move it. */
  lockDate: string | null
  /** Where the company's data lives (changed only by a residency move). */
  residencyZone: ResidencyZone
}

/**
 * POST /platform/tenants/:id/residency-move (OWNER): copies every file to the target zone, verifies
 * each copy's SHA-256, then flips the zone. Leaving OM needs the legal basis of the transfer.
 * `confirmSlug` must equal the tenant's slug.
 */
export const platformResidencyMoveSchema = z
  .object({
    toZone: residencyZoneSchema,
    transferBasis: z.string().trim().min(10).max(1000).nullish(),
    confirmSlug: z.string().trim().min(1).max(63),
  })
  .strict()
export type PlatformResidencyMoveInput = z.input<typeof platformResidencyMoveSchema>

export const RESIDENCY_MOVE_STATUSES = ['ACTIVE', 'DONE', 'FAILED'] as const
export type ResidencyMoveStatus = (typeof RESIDENCY_MOVE_STATUSES)[number]

/** GET /platform/tenants/:id/residency: the zone, files per zone and the latest move. */
export interface PlatformResidencyMove {
  id: string
  fromZone: ResidencyZone
  toZone: ResidencyZone
  status: ResidencyMoveStatus
  transferBasis: string | null
  filesTotal: number
  filesCopied: number
  /** Why it failed, as a stable code (e.g. `HASH_MISMATCH`). */
  failedReason: string | null
  requestedBy: { id: string; name: string } | null
  requestedAt: string
  finishedAt: string | null
}

export interface PlatformTenantResidency {
  zone: ResidencyZone
  /** Zones this deployment can store in (`STORAGE_ZONES`, OM only with `DEPLOYMENT_REGION=OM`). */
  availableZones: ResidencyZone[]
  filesByZone: { zone: ResidencyZone; files: number; bytes: number }[]
  move: PlatformResidencyMove | null
}

/** GET /platform/residency-zones: the zones a new company (or a move) can use on this deployment. */
export interface PlatformResidencyZones {
  availableZones: ResidencyZone[]
}

export interface PlatformTenantUser {
  id: string
  email: string
  name: string
  status: 'ACTIVE' | 'DISABLED'
  roles: string[]
  lastLoginAt: string | null
  /** Locked after too many failed sign-ins (until `lockedUntil`). */
  lockedUntil: string | null
  mustChangePassword: boolean
}

/** The super admin types the new password; the user must replace it at the next sign-in. */
export const platformUserPasswordResetSchema = z.object({ password: passwordSchema })
export type PlatformUserPasswordReset = z.input<typeof platformUserPasswordResetSchema>

/** One operator-audit entry, as the console shows it. */
export interface PlatformActivity {
  id: string
  occurredAt: string
  actor: string
  action: string
  tenantId: string | null
  tenantName: string | null
  details: Record<string, unknown> | null
}

// ── sign in as a user (impersonation) ───────────────────────────────────────

/** "Sign in as": the reason is kept in the operator audit and the company's own audit log. */
export const platformImpersonateSchema = z.object({
  reason: z.string().trim().min(3).max(500),
})
export type PlatformImpersonate = z.input<typeof platformImpersonateSchema>

/** Where the browser goes next: the company's app, now signed in as the user for up to an hour. */
export interface PlatformImpersonation {
  redirect: string
  expiresAt: string
}

// ── subscriptions (record-keeping, not enforced) ────────────────────────────

export const SUBSCRIPTION_STATUSES = ['ACTIVE', 'PAST_DUE', 'CANCELLED', 'EXPIRED'] as const
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number]

const subscriptionFields = z.object({
  status: z.enum(SUBSCRIPTION_STATUSES).default('ACTIVE'),
  startDate: z.iso.date(),
  /** Open-ended when null. */
  endDate: z.iso
    .date()
    .nullish()
    .transform((v) => v ?? null),
  amount: moneySchema.refine((v) => !v.startsWith('-'), 'Cannot be negative'),
  currency: z.string().trim().length(3).toUpperCase().default('OMR'),
  notes: optionalText(2000),
})
const endAfterStart = (v: { startDate: string; endDate: string | null }) =>
  !v.endDate || v.endDate >= v.startDate
const END_BEFORE_START = { path: ['endDate'], message: 'The end date cannot be before the start date' }

export const platformSubscriptionCreateSchema = subscriptionFields
  .extend({ tenantId: z.uuid() })
  .refine(endAfterStart, END_BEFORE_START)
export type PlatformSubscriptionCreate = z.input<typeof platformSubscriptionCreateSchema>

export const platformSubscriptionUpdateSchema = subscriptionFields
  .extend({ version: versionSchema })
  .refine(endAfterStart, END_BEFORE_START)
export type PlatformSubscriptionUpdate = z.input<typeof platformSubscriptionUpdateSchema>

export const PLATFORM_SUBSCRIPTION_SORT_FIELDS = [
  'startDate',
  'endDate',
  'amount',
  'tenant',
  'status',
] as const
export const platformSubscriptionListQuerySchema = pageQuerySchema.extend({
  status: z.enum(SUBSCRIPTION_STATUSES).optional(),
  tenantId: z.uuid().optional(),
  /** Active subscriptions ending within this many days. */
  endingWithin: z.coerce.number().int().min(1).max(365).optional(),
})
export type PlatformSubscriptionListQuery = z.input<typeof platformSubscriptionListQuerySchema>

export interface PlatformSubscription {
  id: string
  tenantId: string
  tenantName: string
  tenantSlug: string
  status: SubscriptionStatus
  startDate: string
  endDate: string | null
  amount: string
  currency: string
  notes: string | null
  version: number
  createdAt: string
}

// ── feedback from companies ─────────────────────────────────────────────────

export const FEEDBACK_TYPES = ['BUG', 'FEATURE_REQUEST', 'GENERAL', 'COMPLAINT'] as const
export type FeedbackType = (typeof FEEDBACK_TYPES)[number]
export const FEEDBACK_STATUSES = ['NEW', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'] as const
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number]

/** POST /feedback: any signed-in user of a company. */
export const feedbackSubmitSchema = z.object({
  type: z.enum(FEEDBACK_TYPES).default('GENERAL'),
  subject: z.string().trim().min(3).max(200),
  message: z.string().trim().min(3).max(5000),
})
export type FeedbackSubmit = z.input<typeof feedbackSubmitSchema>

export const platformFeedbackUpdateSchema = z.object({
  status: z.enum(FEEDBACK_STATUSES),
  adminNote: optionalText(5000),
  version: versionSchema,
})
export type PlatformFeedbackUpdate = z.input<typeof platformFeedbackUpdateSchema>

export const platformFeedbackListQuerySchema = pageQuerySchema.extend({
  status: z.enum(FEEDBACK_STATUSES).optional(),
  type: z.enum(FEEDBACK_TYPES).optional(),
  tenantId: z.uuid().optional(),
})
export type PlatformFeedbackListQuery = z.input<typeof platformFeedbackListQuerySchema>

export interface PlatformFeedback {
  id: string
  tenantId: string
  tenantName: string
  tenantSlug: string
  userName: string | null
  userEmail: string | null
  type: FeedbackType
  subject: string
  message: string
  status: FeedbackStatus
  adminNote: string | null
  version: number
  createdAt: string
  updatedAt: string
}

/** Feedback counts per status, for the inbox's tabs. */
export type PlatformFeedbackCounts = Record<FeedbackStatus, number>

// ── dashboard ───────────────────────────────────────────────────────────────

export interface PlatformDashboard {
  tenants: { active: number; suspended: number; newLast30Days: number }
  users: { active: number }
  subscriptions: {
    active: number
    /** Sum of ACTIVE subscriptions' amounts in OMR (others are listed apart). */
    activeAmountOmr: string
    endingWithin30Days: PlatformSubscription[]
  }
  feedback: { open: number }
  recentTenants: PlatformTenantListItem[]
  recentActivity: PlatformActivity[]
}

/** GET /platform/tenants/:id/message-usage (R0b MS10): messages delivered per month and channel. */
export interface PlatformMessageUsage {
  /** How many months back the counts reach, the current month included. */
  months: number
  /** Newest month first; a month with nothing sent has no row. */
  items: {
    /** `YYYY-MM`, a calendar month in Muscat time. */
    month: string
    channel: MessageChannel
    messages: number
    /** Billable segments (one per e-mail; more for long SMS and WhatsApp messages). */
    segments: number
  }[]
}
